import {redirect} from '@tanstack/react-router';
import type {LocalDate} from '@/features/schedule/domain/local-date.value-object';
import {
  isScheduleCalendarView,
  type ScheduleCalendarView,
} from '@/features/schedule/lib/calendar-view';
import {isLocalDate} from '@/features/schedule/lib/date';

export type ScheduleSearch = {
  /**
   * `day`, `week`, `month`, or `year`. Missing means the default view, so links to
   * `/tracking/calendar` need no search.
   */
  readonly view?: ScheduleCalendarView;
  /**
   * The date the day, week, month, or year is built around. Missing on the first load,
   * which asks the server for the profile-local current week and then replaces the
   * search with `today`.
   */
  readonly anchor?: LocalDate;
};

const parseView = (value: unknown): ScheduleCalendarView | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (typeof value !== 'string' || !isScheduleCalendarView(value)) {
    throw redirect({to: '/tracking/calendar', replace: true});
  }

  return value;
};

const parseAnchor = (value: unknown): LocalDate | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (typeof value !== 'string' || !isLocalDate(value)) {
    throw redirect({to: '/tracking/calendar', replace: true});
  }

  return value;
};

/**
 * `view` defaults to week and `anchor` is optional. An invalid value is stripped from
 * the URL via replace-redirect, like the dashboard date search.
 */
export const scheduleSearch = (search: Record<string, unknown>): ScheduleSearch => {
  return {view: parseView(search.view), anchor: parseAnchor(search.anchor)};
};
